/* eslint-disable max-len */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { Field, useFormikContext, Formik, Form } from 'formik';
import { useHistory } from 'react-router-dom';
import * as Yup from 'yup';
import { Button } from 'primereact/button';
import { useSelector } from 'react-redux';
import { AccountType } from '../../../shared/Constants';
import { validationMessages } from '../../../i18n/common';
import { buildVatIdExampleText } from '../../../utils/AppUtil';
import {
  DropdownWithValidationAndTooltip,
  InputTextWithValidationMessageAndToolip,
} from '../../../components/prime-formik';
import { ClientAgreementCheckbox, GpInvoiceAddressConfirmationCheckbox } from '../../../components/AgreementCheckboxes';
import OrganisationSizeInput from './OrganisationSizeInput';

const messages = defineMessages({
  addressTooltip: { id: 'attrs.address.tooltip', defaultMessage: 'The address of the legal entity' },
  regNoTooltip: {
    id: 'attrs.regNo.tooltip',
    defaultMessage:
      'The official registration number of the legal entity. If there is no registration number please enter "0".',
  },
  vatCountryTooltipNoSelectedCountry: {
    id: 'attrs.vatCountry.tooltip-no-selected-country',
    defaultMessage: 'The country and format of the VAT/GST ID',
  },
  vatCountryTooltipWithSelectedCountry: {
    id: 'attrs.vatCountry.tooltip-with-selected-country',
    defaultMessage: 'Select the GST/VAT format of {selectedCountry}',
  },
  vatIdTooltip: { id: 'attrs.vatId.tooltip', defaultMessage: 'The value of VAT/GST ID' },
  displayNameTooltip: {
    id: 'attrs.displayName.tooltip',
    defaultMessage: 'The name displayed in reports, invitations to candidates etc.',
  },
  dpoEmailTooltip: {
    id: 'attrs.dpoEmail.tooltip',
    defaultMessage: 'The e-mail address of the organisation’s Data Protection Officer',
  },
  email: { id: 'lbl.email', defaultMessage: 'E-mail address' },
  legalName: { id: 'attrs.legalName', defaultMessage: 'Legal Name' },
  legalNameTooltip: { id: 'attrs.legalName.tooltip', defaultMessage: 'The full name of the legal entity' },
  addressLine1: { id: 'attrs.addresLine1', defaultMessage: 'Address Line 1' },
  addressLine2: { id: 'accept-client-account.org-step.addressLine2', defaultMessage: 'Address Line 2 (optional)' },
  postalCode: { id: 'attrs.postalCode', defaultMessage: 'Postal Code' },
  city: { id: 'attrs.city', defaultMessage: 'City' },
  state: { id: 'attrs.state', defaultMessage: 'State (optional)' },
  country: { id: 'attrs.country', defaultMessage: 'Country' },
  registrationNumber: { id: 'attrs.regNo', defaultMessage: 'Registration Number' },
  vatCountry: { id: 'attrs.vatCountry', defaultMessage: 'VAT/GST country' },
  vatId: { id: 'attrs.vatId', defaultMessage: 'VAT/GST ID' },
  vatIdPlaceholder: { id: 'attrs.vatIdPlaceholder', defaultMessage: 'Example: {vatIdExample}' },
  displayName: { id: 'attrs.displayName', defaultMessage: 'Display Name' },
  creditNotificationThreshold: {
    id: 'attrs.creditNotificationThreshold',
    defaultMessage: 'Credit Notification Threshold',
  },
  creditNotificationThresholdTooltip: {
    id: 'attrs.creditNotificationThreshold.tooltip',
    defaultMessage: 'If the credit balance goes below this value, interested parties are notified.',
  },
  dpo: { id: 'attrs.dpo', defaultMessage: 'Data Protection Officer (optional)' },
  next: { id: 'actions.next', defaultMessage: 'Next' },
  previous: { id: 'actions.previous', defaultMessage: 'Previous' },
  decline: { id: 'actions.decline', defaultMessage: 'Decline' },
  vatIdNotValid: {
    id: 'attrs.vatIdNotValid',
    defaultMessage: 'VAT ID is not valid',
  },
});

const OrganizationDetailsStep = ({
  countries,
  taxIdOptions,
  availableTaxIds,
  invitation,
  token,
  userExists,
  ownerInvoiceCountry,
}) => {
  const history = useHistory();
  const { formatMessage } = useIntl();
  const { setFieldValue, values } = useFormikContext();

  const [selectedTaxId, setSelectedTaxId] = useState(null);

  const {
    cldr: { teritoryDisplayName },
  } = useSelector((state) => state.i18n);
  const ownerInvoiceCountryDisplayName = ownerInvoiceCountry ? teritoryDisplayName(ownerInvoiceCountry) : null;

  const {
    type,
    invitedBy: { email, fistName = '', lastName = '' },
  } = invitation;

  const invitationIsForNewBusinessModelAccount =
    type === AccountType.GSC ||
    type === AccountType.GP ||
    type === AccountType.NBM_CLIENT ||
    type === AccountType.ESSENTIAL;
  const invitationRequiresTaxInformation = invitationIsForNewBusinessModelAccount || type === AccountType.MCP;
  const invitationIsForGp = type === AccountType.GP;

  const initialValues = {
    company: values.company,
    organisationSize: values.organisationSize,
    client_terms_of_use_agreement: values.client_terms_of_use_agreement,
    gp_invoice_address_confirmation: values.gp_invoice_address_confirmation,
  };

  const taxIdValidationSchema = (() => {
    if (invitationRequiresTaxInformation) {
      if (selectedTaxId) {
        return Yup.string()
          .required(formatMessage(validationMessages.required))
          .matches(new RegExp(selectedTaxId.regularExpression), formatMessage(messages.vatIdNotValid))
          .nullable();
      } else {
        return Yup.string()
          .required(formatMessage(validationMessages.required))
          .nullable();
      }
    } else {
      if (selectedTaxId) {
        return Yup.string()
          .matches(new RegExp(selectedTaxId.regularExpression), formatMessage(messages.vatIdNotValid))
          .nullable();
      } else {
        return Yup.string().nullable();
      }
    }
  })();

  const validationSchema = Yup.object().shape({
    client_terms_of_use_agreement: Yup.boolean()
      .required(formatMessage(validationMessages.fieldMustBeChecked))
      .oneOf([true], formatMessage(validationMessages.fieldMustBeChecked))
      .default(false),
    gp_invoice_address_confirmation: Yup.boolean().when([], {
      is: () => invitationIsForGp && ownerInvoiceCountryDisplayName,
      then: Yup.boolean()
        .required(formatMessage(validationMessages.fieldMustBeChecked))
        .oneOf([true], formatMessage(validationMessages.fieldMustBeChecked))
        .default(false),
      otherwise: Yup.boolean(),
    }),
    organisationSize: Yup.number().when([], {
      is: () => invitationIsForNewBusinessModelAccount,
      then: Yup.number().required(),
      otherwise: Yup.number().nullable(),
    }),
    company: Yup.object()
      .shape({
        displayName: Yup.string()
          .min(2, formatMessage(validationMessages.tooShort, { value: 2 }))
          .max(50, formatMessage(validationMessages.tooLong, { value: 50 }))
          .required(formatMessage(validationMessages.required))
          .default(''),
        legalName: Yup.string()
          .min(2, formatMessage(validationMessages.tooShort, { value: 2 }))
          .max(50, formatMessage(validationMessages.tooLong, { value: 50 }))
          .required(formatMessage(validationMessages.required))
          .default(''),
        addressLine1: Yup.string()
          .required(formatMessage(validationMessages.required))
          .default(''),
        addressLine2: Yup.string().default(''),
        city: Yup.string()
          .min(2, formatMessage(validationMessages.tooShort, { value: 2 }))
          .max(50, formatMessage(validationMessages.tooLong, { value: 50 }))
          .default(''),
        country: Yup.string()
          .min(2, formatMessage(validationMessages.tooShort, { value: 2 }))
          .max(50, formatMessage(validationMessages.tooLong, { value: 50 }))
          .default(''),
        postalCode: Yup.string()
          .min(2, formatMessage(validationMessages.tooShort, { value: 2 }))
          .max(50, formatMessage(validationMessages.tooLong, { value: 50 }))
          .default(''),
        registrationNumber: Yup.string()
          .required(formatMessage(validationMessages.required))
          .default(''),
        taxIdType: Yup.string().when([], {
          is: () => invitationRequiresTaxInformation,
          then: Yup.string()
            .required(formatMessage(validationMessages.required))
            .default(''),
          otherwise: Yup.string().nullable(),
        }),
        taxId: taxIdValidationSchema,
        state: Yup.string().default(''),
        dpoEmail: Yup.string().default(''),
        creditNotificationThreshold: Yup.number().when([], {
          is: () => !invitationIsForNewBusinessModelAccount,
          then: Yup.number()
            .required(formatMessage(validationMessages.required))
            .default(1),
          otherwise: Yup.number().nullable(),
        }),
      })
      .required(formatMessage(validationMessages.required)),
  });

  const vatIdPlaceholderExample = buildVatIdExampleText(selectedTaxId);

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        setFieldValue('company', { ...values.company });
        setFieldValue('client_terms_of_use_agreement', values.client_terms_of_use_agreement);

        let redirectUrl = `/account-invitation/other-users?token=${token}`;

        redirectUrl = userExists ? `${redirectUrl}&userExists=true` : redirectUrl;

        history.push(redirectUrl);
      }}
    >
      {({ setFieldValue, values, isValid, isSubmitting, handleSubmit }) => {
        const isNextDisabled = !isValid || isSubmitting;
        const vatCountryTooltip = values.company.country
          ? formatMessage(messages.vatCountryTooltipWithSelectedCountry, {
              selectedCountry: countries.find((country) => country.value === values.company.country).label,
            })
          : formatMessage(messages.vatCountryTooltipNoSelectedCountry);

        return (
          <Form onSubmit={handleSubmit}>
            <div className="p-col-12">
              <FormattedMessage
                id="accept-account-invitation.form.heading-title"
                defaultMessage="Hello, {invitee},"
                tagName="h2"
                values={{
                  invitee: invitation.fullName,
                }}
              />
              <FormattedMessage
                id="accept-account-invitation.message"
                defaultMessage="You have been invited by {inviterFullName} ({inviterEmail}) from {partnerAccountName} to create a new account. Please follow the instructions bellow."
                tagName="p"
                values={{
                  inviterFullName: <strong>{`${fistName} ${lastName}`}</strong>,
                  inviterEmail: email,
                  partnerAccountName: <strong>{invitation.partner.displayName}</strong>,
                }}
              />
            </div>
            <div className="p-col-12">
              <FormattedMessage
                id="accept-account-invitation.org-details.heading"
                defaultMessage="Organisational details"
                tagName="h2"
              />
              <FormattedMessage
                id="accept-account-invitation.org-details.instructions"
                defaultMessage="Please enter your organisation's legal name, invoice address and other account specific information."
                tagName="p"
              />
            </div>
            <div className="p-col-12">
              <Field
                name="company.legalName"
                labelText={formatMessage(messages.legalName)}
                component={InputTextWithValidationMessageAndToolip}
                tooltipText={formatMessage(messages.legalNameTooltip)}
              />
            </div>
            <div className="p-col-12">
              <Field
                name="company.addressLine1"
                labelText={formatMessage(messages.addressLine1)}
                component={InputTextWithValidationMessageAndToolip}
                tooltipText={formatMessage(messages.addressTooltip)}
              />
            </div>
            <div className="p-col-12">
              <Field
                name="company.addressLine2"
                labelText={formatMessage(messages.addressLine2)}
                component={InputTextWithValidationMessageAndToolip}
                tooltipText={formatMessage(messages.addressTooltip)}
              />
            </div>
            <div className="p-col-12">
              <Field
                name="company.postalCode"
                labelText={formatMessage(messages.postalCode)}
                component={InputTextWithValidationMessageAndToolip}
                tooltipText={formatMessage(messages.addressTooltip)}
              />
            </div>
            <div className="p-col-12">
              <Field
                name="company.city"
                labelText={formatMessage(messages.city)}
                component={InputTextWithValidationMessageAndToolip}
                tooltipText={formatMessage(messages.addressTooltip)}
              />
            </div>
            <div className="p-col-12">
              <Field
                name="company.state"
                labelText={formatMessage(messages.state)}
                component={InputTextWithValidationMessageAndToolip}
                tooltipText={formatMessage(messages.addressTooltip)}
              />
            </div>
            <div className="p-col-12">
              <label className="field-label" htmlFor="company.country">
                <FormattedMessage id="accept-client-account.org-step.country" defaultMessage="Country" />
              </label>
              {ownerInvoiceCountryDisplayName ? (
                <strong>{ownerInvoiceCountryDisplayName}</strong>
              ) : (
                <Field
                  id="company.country"
                  name="company.country"
                  labelText={formatMessage(messages.country)}
                  filter
                  filterBy="label,value"
                  options={countries}
                  component={DropdownWithValidationAndTooltip}
                  tooltipText={formatMessage(messages.addressTooltip)}
                />
              )}
            </div>
            <div className="p-col-12">
              <Field
                name="company.registrationNumber"
                labelText={formatMessage(messages.registrationNumber)}
                component={InputTextWithValidationMessageAndToolip}
                tooltipText={formatMessage(messages.regNoTooltip)}
              />
            </div>
            <div className="p-col-12">
              <label className="field-label" htmlFor="company.taxIdType">
                <FormattedMessage id="attrs.vatCountry" defaultMessage="VAT/GST country" />
              </label>
              <Field
                id="company.taxIdType"
                name="company.taxIdType"
                labelText={formatMessage(messages.vatCountry)}
                filter
                filterBy="label,value"
                options={taxIdOptions}
                component={DropdownWithValidationAndTooltip}
                tooltipText={vatCountryTooltip}
                onChange={(e) => {
                  setFieldValue('company.taxIdType', e.target.value);

                  const taxInformation = availableTaxIds.find((availableTaxId) => availableTaxId.id === e.target.value);

                  if (taxInformation) {
                    setSelectedTaxId(taxInformation);
                  }
                }}
              />
            </div>
            <div className="p-col-12">
              <Field
                name="company.taxId"
                labelText={formatMessage(messages.vatId)}
                placeholderText={formatMessage(messages.vatIdPlaceholder, { vatIdExample: vatIdPlaceholderExample })}
                tooltipText={formatMessage(messages.vatIdTooltip)}
                component={InputTextWithValidationMessageAndToolip}
              />
            </div>
            <div className="p-col-12">
              <Field
                name="company.displayName"
                labelText={formatMessage(messages.displayName)}
                component={InputTextWithValidationMessageAndToolip}
                tooltipText={formatMessage(messages.displayNameTooltip)}
              />
            </div>
            <div className="p-col-12">
              <Field
                name="company.dpoEmail"
                type="email"
                labelText={formatMessage(messages.dpo)}
                component={InputTextWithValidationMessageAndToolip}
                tooltipText={formatMessage(messages.dpoEmailTooltip)}
              />
            </div>
            {invitationIsForNewBusinessModelAccount && <OrganisationSizeInput />}
            {!invitationIsForNewBusinessModelAccount && (
              <div className="p-col-12">
                <Field
                  name="company.creditNotificationThreshold"
                  type="number"
                  min="1"
                  labelText={formatMessage(messages.creditNotificationThreshold)}
                  component={InputTextWithValidationMessageAndToolip}
                  tooltipText={formatMessage(messages.creditNotificationThresholdTooltip)}
                />
              </div>
            )}
            <div className="p-col-12">
              <Field
                id="client_terms_of_use_agreement"
                name="client_terms_of_use_agreement"
                invitationIsForGp={invitationIsForGp}
                component={ClientAgreementCheckbox}
              />
            </div>
            {invitationIsForGp && ownerInvoiceCountryDisplayName && (
              <div className="p-col-12">
                <Field
                  id="gp_invoice_address_confirmation"
                  name="gp_invoice_address_confirmation"
                  ownerInvoiceCountryDisplayName={ownerInvoiceCountryDisplayName}
                  component={GpInvoiceAddressConfirmationCheckbox}
                />
              </div>
            )}
            <div>
              {userExists ? (
                <Button
                  type="button"
                  label={formatMessage(messages.decline)}
                  onClick={() => history.push(`/account-invitation-decline?token=${token}`)}
                />
              ) : (
                <Button type="button" label={formatMessage(messages.previous)} onClick={() => history.goBack()} />
              )}
              <Button type="submit" label={formatMessage(messages.next)} disabled={isNextDisabled} />
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

OrganizationDetailsStep.propTypes = {
  invitation: PropTypes.objectOf(PropTypes.any).isRequired,
  countries: PropTypes.arrayOf(PropTypes.any).isRequired,
  taxIdOptions: PropTypes.arrayOf(PropTypes.any).isRequired,
  token: PropTypes.string.isRequired,
  userExists: PropTypes.bool.isRequired,
};

export default OrganizationDetailsStep;
